// Sticker placement for `scene` themes, ported 1:1 from the app's
// lib/Games/general/quick/twenty_challenge/twenty_challenge_layout.dart
// (main @ c311cc47). Keep both in step: test/layout.test.ts compares this
// port against fixtures the Dart planner produced (see test/fixtures/README.md).
//
// Every won item gets its own rect, laid out by the theme's archetype
// (room | outdoor | host | plate). Deterministic, no DOM. Free parameters
// (row counts, splits, zones, grid columns) are searched to maximise the
// smallest item. The comments of the Dart file explain the rules; only the
// mechanics are repeated here.

import { Offset, Rect, clamp } from './geom';

export interface LayoutItem {
  itemIndex: number;
  box: Rect; // baked bbox on the canvas
  kind: string; // floor | wall | ceiling | sky | water | centre
  sizeFactor: number; // S 0.55, M 0.8, L 1.0, XL 1.35
}

const bakedCx = (i: LayoutItem) => i.box.center.dx;
const itemAspect = (i: LayoutItem) => (i.box.height <= 0 ? 1 : i.box.width / i.box.height);

export interface PlacedItem {
  itemIndex: number;
  rect: Rect;
  drawOrder: number;
}

export interface LayoutSpec {
  archetype: string; // room | outdoor | host | plate
  canvas: { width: number; height: number };
  horizon?: number | null;
  keepout?: Rect | null;
  host?: Rect | null;
  surface?: Rect | null;
  surfaceIsEllipse?: boolean;
  lineup?: boolean;
}

const MAX_COVER = 0.12;
const FRONT_COVER = 0.2;
const UNIT_SLACK = 1.25;

function coversTooMuch(a: Rect, b: Rect): boolean {
  const i = a.intersect(b);
  if (i.width <= 0 || i.height <= 0) return false;
  if (i.width * i.height <= MAX_COVER * a.width * a.height + 1e-6) return false;
  return !(i.top >= a.bottom - FRONT_COVER * a.height - 1e-6 && b.bottom >= a.bottom - 1e-6);
}

const cmp = (a: number, b: number) => (a < b ? -1 : a > b ? 1 : 0);

export function planLayout(spec: LayoutSpec, items: LayoutItem[]): PlacedItem[] {
  if (items.length === 0) return [];
  const fMax = Math.max(...items.map((i) => i.sizeFactor));
  let unit: number | null = null;
  const planWith = (opts: { lineup?: boolean; straightRows?: boolean } = {}): Plan => {
    const p = new Planner(opts.lineup ? lined(spec) : spec, fMax, unit, opts.straightRows ?? false);
    if (spec.archetype === 'host' && spec.host) return p.host(items);
    if (spec.archetype === 'plate' && spec.surface) return p.plate(items);
    return p.stage(items);
  };

  const units = planWith({ lineup: true })
    .ps.map((q) => readable(q.rect) / (q.item.sizeFactor * q.depth))
    .sort(cmp);
  unit = Math.max(units[0], units[Math.floor(units.length / 2)] / 1.6);
  const bounds = Rect.fromLTWH(0, 0, spec.canvas.width, spec.canvas.height);
  let plan = planWith();
  let rects = nudged(spec, plan, bounds);
  const k = spec.archetype === 'room' && !spec.lineup ? spec.keepout ?? null : null;
  if (k) {
    const straight = new Map<number, number>();
    for (const q of planWith({ lineup: true }).ps) {
      straight.set(q.item.itemIndex, area(clampInto(q.rect, bounds).intersect(k)));
    }
    let more = false;
    for (let i = 0; i < rects.length && !more; i++) {
      more = area(rects[i].intersect(k)) > (straight.get(plan.ps[i].item.itemIndex) ?? 0) + 1e-6;
    }
    if (more) {
      plan = planWith({ straightRows: true });
      rects = nudged(spec, plan, bounds);
    }
  }
  return plan.ps.map((p, i) => ({ itemIndex: p.item.itemIndex, rect: rects[i], drawOrder: i }));
}

const FLOATERS = new Set(['ceiling', 'sky', 'wall', 'centre']);

function nudged(spec: LayoutSpec, plan: Plan, bounds: Rect): Rect[] {
  const rects = plan.ps.map((q) => clampInto(q.rect, bounds));
  const hr = spec.archetype === 'host' ? spec.host ?? null : null;
  const guard: Rect[] = [];
  if (hr) {
    guard.push(hr, Rect.fromLTRB(hr.left, hr.top - 0.05 * spec.canvas.height, hr.right, hr.top));
  }
  if (spec.archetype === 'room' && spec.keepout) guard.push(spec.keepout);
  const floorLine =
    spec.archetype === 'room'
      ? (spec.horizon ?? spec.canvas.height * 0.55) + spec.canvas.height * 0.02
      : null;
  const offFloor = (i: number, r: Rect) =>
    floorLine !== null && !FLOATERS.has(plan.ps[i].item.kind) && r.bottom < floorLine;
  const onHost = (from: Rect, to: Rect) =>
    guard.some((g) => area(to.intersect(g)) > area(from.intersect(g)) + 1e-6);
  const nudge = (i: number, d: Offset): Rect => {
    for (const v of [d, new Offset(0, d.dy), new Offset(d.dx, 0)]) {
      for (const f of [1.0, 0.75, 0.5, 0.25]) {
        const r = clampInto(rects[i].shift(v.times(f)), bounds);
        if (!clashes(rects, i, r) && !onHost(rects[i], r) && !offFloor(i, r)) return r;
      }
    }
    return rects[i];
  };

  for (let i = 0; i < rects.length; i++) {
    if (!plan.ps[i].nudge.equals(Offset.zero)) rects[i] = nudge(i, plan.ps[i].nudge);
  }

  const ground =
    spec.archetype === 'room' || spec.archetype === 'outdoor'
      ? (spec.horizon ?? spec.canvas.height * (spec.archetype === 'outdoor' ? 0.5 : 0.55)) +
        spec.canvas.height * 0.02
      : null;
  const offGround = (i: number, r: Rect) =>
    ground !== null && !FLOATERS.has(plan.ps[i].item.kind) && r.bottom < ground;
  const step = spec.canvas.width * 0.02;
  const minL = spec.canvas.width * 0.1;
  for (let i = 0; i < rects.length; i++) {
    if (!clashes(rects, i, rects[i])) continue;
    let best: Rect | null = null;
    for (const f of [1.0, 0.85, 0.7, 0.55]) {
      const at = shrink(rects[i], f, new Offset(0.5, 0.5));
      if (f < 1 && long(at) < minL - 0.5) break;
      let bestD = Infinity;
      for (let ring = 0; ring <= 50 && best === null; ring++) {
        for (let dx = -ring; dx <= ring; dx++) {
          for (let dy = -ring; dy <= ring; dy++) {
            if (Math.max(Math.abs(dx), Math.abs(dy)) !== ring) continue;
            const r = clampInto(at.shift(new Offset(dx * step, dy * step)), bounds);
            const d = r.center.minus(rects[i].center).distance;
            if (d < bestD && !clashes(rects, i, r) && !onHost(rects[i], r) && !offGround(i, r)) {
              best = r;
              bestD = d;
            }
          }
        }
      }
      if (best !== null) break;
    }
    if (best !== null) rects[i] = best;
  }
  return rects;
}

function lined(s: LayoutSpec): LayoutSpec {
  return { ...s, lineup: true };
}

function clashes(rs: Rect[], i: number, r: Rect): boolean {
  for (let j = 0; j < rs.length; j++) {
    if (j < i && coversTooMuch(rs[j], r)) return true;
    if (j > i && coversTooMuch(r, rs[j])) return true;
  }
  return false;
}

const BACK = [-0.15, -0.85, -0.4, -1.0, -0.25, -0.7];
const EITHER = [0.0, -0.8, 0.5, -0.3, 1.0, -0.6];
const SPREAD = [0.3, -0.5, 0.2, -0.4, 0.5, -0.2];
const LONE = [-0.18, 0.16, -0.1];

class P {
  constructor(
    readonly item: LayoutItem,
    readonly rect: Rect,
    readonly anchor: Offset,
    readonly nudge: Offset = Offset.zero,
    readonly alone: boolean = false,
    readonly depth: number = 1,
  ) {}

  at(r: Rect): P {
    return new P(this.item, r, this.anchor, this.nudge, this.alone, this.depth);
  }
}

class Plan {
  constructor(
    readonly ps: P[],
    readonly bad: number,
    readonly sides: number[], // readable sizes, ascending
  ) {}

  get minSide(): number {
    return this.sides.length === 0 ? Infinity : this.sides[0];
  }

  get logSum(): number {
    return this.sides.reduce((a, e) => a + Math.log(e), 0);
  }

  beats(o: Plan | null): boolean {
    if (o === null || this.bad !== o.bad) return o === null || this.bad < o.bad;
    if (Math.abs(this.minSide - o.minSide) > 0.5) return this.minSide > o.minSide;
    return this.logSum > o.logSum + 1e-9;
  }

  leximinOver(o: Plan, tol: number): boolean {
    if (this.bad !== o.bad) return this.bad < o.bad;
    for (let i = 0; i < this.sides.length && i < o.sides.length; i++) {
      const d = this.sides[i] - o.sides[i];
      if (Math.abs(d) > tol) return d > 0;
    }
    return false;
  }
}

type Cap = ((it: LayoutItem) => number) | null;

interface BandOpts {
  vertical?: boolean;
  align?: number;
  maxRows?: number;
  justify?: boolean;
  cap?: Cap;
  wobble?: number[];
}

interface StripOpts extends BandOpts {
  stagger?: number;
  depth?: number;
}

class Planner {
  readonly w: number;
  readonly h: number;
  readonly g: number;
  readonly minL: number;
  readonly goodL: number;
  readonly maxL: number;
  readonly wobbly: boolean;

  constructor(
    readonly spec: LayoutSpec,
    readonly fMax: number,
    readonly unit: number | null,
    readonly straightRows: boolean,
  ) {
    this.wobbly = !spec.lineup && ['room', 'outdoor', 'host'].includes(spec.archetype);
    this.w = spec.canvas.width;
    this.h = spec.canvas.height;
    this.g = spec.canvas.width * 0.025;
    this.minL = spec.canvas.width * 0.1;
    this.goodL = spec.canvas.width * 0.13;
    this.maxL = spec.canvas.width * 0.46;
  }

  wob(pattern: number[], amp: number): number[] {
    return this.wobbly ? pattern.map((v) => v * amp) : [];
  }

  alternate(ps: P[]): P[] {
    let c = 0;
    return ps.map((p) =>
      p.alone
        ? new P(p.item, p.rect, p.anchor, new Offset(LONE[c++ % LONE.length] * this.w, 0), false, p.depth)
        : p,
    );
  }

  // ---- room / outdoor ----------------------------------------------------

  stage(items: LayoutItem[]): Plan {
    const { w, h, g } = this;
    const outdoor = this.spec.archetype === 'outdoor';
    const hY = this.spec.horizon ?? h * (outdoor ? 0.5 : 0.55);
    const top: LayoutItem[] = [];
    const mid: LayoutItem[] = [];
    const water: LayoutItem[] = [];
    const floor: LayoutItem[] = [];
    for (const it of items) {
      if (it.kind === 'ceiling' || it.kind === 'sky') top.push(it);
      else if (it.kind === 'wall' || it.kind === 'centre') mid.push(it);
      else if (it.kind === 'water' && outdoor) water.push(it);
      else floor.push(it);
    }
    const topZone = Rect.fromLTRB(
      w * 0.02,
      h * 0.03,
      w * 0.98,
      Math.max(h * 0.18, Math.min(h * 0.3, hY - h * 0.12)),
    );
    const rises = top.length + mid.length <= 1 && water.length === 0 ? [false, true] : [false];
    const grow = mid.length === 0 && water.length === 0;

    let best: Plan | null = null;
    for (const s of [1.0, 0.85, 0.7]) {
      const waterPs = this.band(
        water,
        Rect.fromLTRB(w * 0.02, hY + h * 0.1 - h * 0.22 * s, w * 0.98, hY + h * 0.1),
        { align: 1, maxRows: 1, wobble: this.wob(BACK, 0.45) },
      );
      const floorTop =
        waterPs.length === 0 ? null : Math.max(...waterPs.map((p) => p.rect.bottom)) + g;
      for (const rise of rises) {
        const floorPs = this.floor(floor, hY, s, floorTop, rise, grow);
        const cap = this.capFrom(floorPs);
        const topPs = this.band(top, topZone, {
          align: 0,
          maxRows: 2,
          cap,
          wobble: this.wob(EITHER, 0.25),
        });
        const midTop = topPs.length === 0 ? h * 0.03 : Math.max(...topPs.map((p) => p.rect.bottom)) + g;
        const low = [...waterPs, ...floorPs];
        let midBottom = hY + h * 0.04;
        if (low.length > 0) {
          const lowTop = Math.min(...low.map((p) => p.rect.top));
          midBottom = Math.min(midBottom, lowTop + 0.25 * (lowTop - midTop));
        }
        midBottom = Math.min(Math.max(midBottom, midTop + this.goodL), h * 0.98);
        const midZone = Rect.fromLTRB(w * 0.02, midTop, w * 0.98, midBottom);
        for (const midPs of this.midOptions(mid, midZone, cap)) {
          const plan = this.resolve(this.alternate([...topPs, ...midPs, ...waterPs, ...floorPs]));
          if (plan.beats(best)) best = plan;
        }
      }
      if (floor.length === 0 && water.length === 0) break;
    }
    return best!;
  }

  capFrom(floorPs: P[]): Cap {
    if (floorPs.length === 0) return null;
    const units = floorPs.map((p) => readable(p.rect) / p.item.sizeFactor).sort(cmp);
    const unit = units[Math.floor(units.length / 2)];
    return (it) => 1.25 * unit * it.sizeFactor;
  }

  *midOptions(mid: LayoutItem[], zone: Rect, cap: Cap): Generator<P[]> {
    const { w, g, goodL } = this;
    const k = this.spec.keepout ?? null;
    if (mid.length === 0) {
      yield [];
      return;
    }
    if (k === null || !k.overlaps(zone)) {
      yield this.band(mid, zone, { cap, wobble: this.wob(EITHER, 0.25) });
      return;
    }
    const byX = [...mid].sort((a, b) => cmp(bakedCx(a), bakedCx(b)));
    const left = Rect.fromLTRB(zone.left, zone.top, k.left - g, zone.bottom);
    const right = Rect.fromLTRB(k.right + g, zone.top, zone.right, zone.bottom);
    const above = Rect.fromLTRB(zone.left, zone.top, zone.right, Math.min(k.top - g, zone.bottom));
    const below = Rect.fromLTRB(zone.left, Math.max(k.bottom + g, zone.top), zone.right, zone.bottom);
    const spill = above.height >= goodL ? above : below.height >= goodL ? below : zone;
    for (let sp = 0; sp <= byX.length; sp++) {
      const ps: P[] = [];
      const over: LayoutItem[] = [];
      const pairs: [LayoutItem[], Rect][] = [
        [byX.slice(0, sp), left],
        [byX.slice(sp), right],
      ];
      for (const [group, slot] of pairs) {
        if (group.length === 0) continue;
        if (slot.width < w * 0.16) {
          over.push(...group);
        } else {
          ps.push(...this.band(group, slot, { cap, wobble: this.wob(EITHER, 0.25) }));
        }
      }
      yield [...ps, ...this.band(over, spill, { cap, wobble: this.wob(EITHER, 0.25) })];
    }
  }

  floor(
    floor: LayoutItem[],
    hY: number,
    s: number,
    top: number | null,
    rise: boolean,
    grow: boolean,
  ): P[] {
    if (floor.length === 0) return [];
    const { h, w } = this;
    const byDepth = [...floor].sort((a, b) => {
      const c = cmp(a.box.bottom, b.box.bottom);
      return c !== 0 ? c : cmp(bakedCx(a), bakedCx(b));
    });
    const n = byDepth.length;
    const front = h * 0.99;
    const back = rise ? hY : hY + 0.3 * (front - hY);
    let best: Plan | null = null;
    for (let k = 1; k <= Math.min(3, n); k++) {
      for (const cuts of cutsOf(n, k)) {
        const bounds = [0, ...cuts, n];
        const ps: P[] = [];
        const rowHeight = (f: number) =>
          (k === 1
            ? grow
              ? Math.min(front - hY, h * 0.42)
              : h * 0.32
            : (rise ? 0.28 + 0.04 * f : 0.2 + 0.12 * f) *
              (this.spec.archetype === 'outdoor' ? 0.75 + 0.25 * f : 1) *
              h) * s;
        for (let i = 0; i < k; i++) {
          const f = k === 1 ? 1.0 : i / (k - 1);
          let base = back + (front - back) * f;
          let rowH = rowHeight(f);
          const depth = Math.sqrt(rowH / rowHeight(1));
          if (top !== null && base - rowH < top) {
            base = Math.min(front, top + rowH);
            rowH = Math.max(base - top, this.minL);
          }
          const group = byDepth.slice(bounds[i], bounds[i + 1]).sort((a, b) => cmp(bakedCx(a), bakedCx(b)));
          const wobPattern: number[] = [];
          for (let j = 0; j < 6; j++) wobPattern.push(BACK[(j + 3 * (k - 1 - i)) % 6]);
          ps.push(
            ...this.strip(group, Rect.fromLTRB(w * 0.02, base - rowH, w * 0.98, base), {
              align: 1,
              stagger: i === k - 1 ? 0 : this.wobbly && !this.straightRows && i % 2 === 1 ? -1 : 1,
              wobble: this.wob(wobPattern, i === k - 1 ? 0.45 : 0.35),
              depth,
            }),
          );
        }
        const plan = this.resolve(ps);
        if (plan.beats(best)) best = plan;
      }
    }
    return best!.ps;
  }

  // ---- host --------------------------------------------------------------

  host(items: LayoutItem[]): Plan {
    const { w, h, g } = this;
    const hr = this.spec.host!;
    const zTop = Rect.fromLTRB(w * 0.02, h * 0.02, w * 0.98, hr.top + h * 0.04);
    const zBottom = Rect.fromLTRB(w * 0.02, hr.bottom - h * 0.04, w * 0.98, h * 0.98);
    const colL: [number, number] = [w * 0.02, hr.left + w * 0.04];
    const colR: [number, number] = [hr.right - w * 0.04, w * 0.98];
    const ok = [
      zTop.height >= h * 0.12,
      colR[1] - colR[0] >= w * 0.12,
      zBottom.height >= h * 0.12,
      colL[1] - colL[0] >= w * 0.12,
    ];
    if (!ok.includes(true)) return this.stage(items);

    const evalZones = (zoneOf: number[]): Plan => {
      const groups: LayoutItem[][] = [[], [], [], []];
      for (let i = 0; i < items.length; i++) groups[zoneOf[i]].push(items[i]);
      const colTop = groups[0].length === 0 ? h * 0.02 : zTop.bottom + g;
      const colBottom = groups[2].length === 0 ? h * 0.98 : zBottom.top - g;
      return this.resolve(
        this.alternate([
          ...this.band(groups[0], zTop, { align: 1, justify: true, wobble: this.wob([-1, 0], 0.2) }),
          ...this.band(groups[3], Rect.fromLTRB(colL[0], colTop, colL[1], colBottom), {
            vertical: true,
            align: 1,
            maxRows: 2,
            justify: true,
            wobble: this.wob([0, -1], 0.7),
          }),
          ...this.band(groups[1], Rect.fromLTRB(colR[0], colTop, colR[1], colBottom), {
            vertical: true,
            align: 0,
            maxRows: 2,
            justify: true,
            wobble: this.wob([0, 1], 0.7),
          }),
          ...this.band(groups[2], zBottom, { align: 0, justify: true, wobble: this.wob([1, 0], 0.2) }),
        ]),
      );
    };

    let zoneOf = items.map((it) => {
      const d = it.box.center.minus(hr.center);
      const nx = d.dx / Math.max(1.0, hr.width / 2);
      const ny = d.dy / Math.max(1.0, hr.height / 2);
      const xz = nx < 0 ? 3 : 1;
      const yz = ny < 0 ? 0 : 2;
      const prefs = Math.abs(nx) >= Math.abs(ny) ? [xz, yz, 4 - xz, 2 - yz] : [yz, xz, 2 - yz, 4 - xz];
      const z = prefs.find((z) => ok[z]);
      if (z === undefined) throw new Error('no zone');
      return z;
    });
    let cur = evalZones(zoneOf);
    for (let iter = 0; iter < 30; iter++) {
      let next: number[] | null = null;
      let bestP: Plan | null = null;
      for (let i = 0; i < items.length; i++) {
        for (let z = 0; z < 4; z++) {
          if (!ok[z] || z === zoneOf[i]) continue;
          const cand = [...zoneOf];
          cand[i] = z;
          const p = evalZones(cand);
          if (p.leximinOver(cur, w * 0.01) && (bestP === null || p.leximinOver(bestP, 0))) {
            bestP = p;
            next = cand;
          }
        }
      }
      if (next === null) break;
      zoneOf = next;
      cur = bestP!;
    }
    return cur;
  }

  // ---- plate -------------------------------------------------------------

  plate(items: LayoutItem[]): Plan {
    let sr = this.spec.surface!;
    if (this.spec.surfaceIsEllipse) {
      sr = Rect.fromCenter(sr.center, sr.width * 0.84, sr.height * 0.84);
    }
    const byY = [...items].sort((a, b) => {
      const c = cmp(a.box.center.dy, b.box.center.dy);
      return c !== 0 ? c : cmp(bakedCx(a), bakedCx(b));
    });
    const n = byY.length;
    let best: Plan | null = null;
    for (let c = 1; c <= n; c++) {
      const r = Math.floor((n + c - 1) / c);
      const cw = sr.width / c;
      const ch = sr.height / r;
      const ps: P[] = [];
      for (let row = 0; row < r; row++) {
        const group = byY.slice(row * c, Math.min(n, (row + 1) * c)).sort((a, b) => cmp(bakedCx(a), bakedCx(b)));
        const x0 = sr.left + ((c - group.length) * cw) / 2;
        for (let j = 0; j < group.length; j++) {
          const it = group[j];
          const a = aspectOf(it);
          const lam = Math.max(1.0, a);
          const eta = etaOf(a);
          const full = Math.min(cw / a, ch);
          let ih = Math.min(0.92 * full, (0.92 * Math.min(cw, ch) * it.sizeFactor) / (this.fMax * eta));
          if (this.unit !== null) ih = Math.min(ih, (UNIT_SLACK * this.unit * it.sizeFactor) / eta);
          if (ih * lam > this.maxL) ih = this.maxL / lam;
          if (ih * eta < this.minL) ih = Math.min(this.minL / eta, full);
          ps.push(
            new P(
              it,
              Rect.fromCenter(new Offset(x0 + (j + 0.5) * cw, sr.top + (row + 0.5) * ch), ih * a, ih),
              new Offset(0.5, 0.5),
            ),
          );
        }
      }
      const plan = this.resolve(ps);
      if (plan.beats(best)) best = plan;
    }
    return best!;
  }

  // ---- packing -----------------------------------------------------------

  band(items: LayoutItem[], zone: Rect, o: BandOpts = {}): P[] {
    const vertical = o.vertical ?? false;
    const align = o.align ?? 0.5;
    const maxRows = o.maxRows ?? 3;
    const justify = o.justify ?? false;
    const cap = o.cap ?? null;
    const wobble = o.wobble ?? [];
    if (items.length === 0) return [];
    const across = (i: LayoutItem) => (vertical ? bakedCx(i) : i.box.center.dy);
    const along = (i: LayoutItem) => (vertical ? i.box.center.dy : bakedCx(i));
    const sorted = [...items].sort((a, b) => {
      const c = cmp(across(a), across(b));
      return c !== 0 ? c : cmp(along(a), along(b));
    });
    const n = sorted.length;
    const span = vertical ? zone.width : zone.height;
    let best: Plan | null = null;
    for (let k = 1; k <= Math.min(maxRows, n); k++) {
      const line = (span - this.g * (k - 1)) / k;
      if (k > 1 && line < this.minL) break;
      const ps: P[] = [];
      for (let i = 0; i < k; i++) {
        const group = sorted
          .slice(Math.floor((i * n) / k), Math.floor(((i + 1) * n) / k))
          .sort((a, b) => cmp(along(a), along(b)));
        const off = i * (line + this.g);
        ps.push(
          ...this.strip(
            group,
            vertical
              ? Rect.fromLTWH(zone.left + off, zone.top, line, zone.height)
              : Rect.fromLTWH(zone.left, zone.top + off, zone.width, line),
            { vertical, align: k === 1 ? align : 0.5, justify, cap, wobble },
          ),
        );
      }
      const plan = this.resolve(ps);
      if (plan.beats(best)) best = plan;
    }
    return best!.ps;
  }

  strip(items: LayoutItem[], zone: Rect, o: StripOpts = {}): P[] {
    const vertical = o.vertical ?? false;
    const align = o.align ?? 0.5;
    const justify = o.justify ?? false;
    const stagger = o.stagger ?? 0;
    const cap = o.cap ?? null;
    let wobble = o.wobble ?? [];
    const depth = o.depth ?? 1;
    const n = items.length;
    if (n === 0) return [];
    const { minL, maxL, goodL } = this;
    if (zone.width < minL || zone.height < minL) {
      zone = Rect.fromCenter(zone.center, Math.max(zone.width, minL), Math.max(zone.height, minL));
    }
    const cross = vertical ? zone.width : zone.height;
    const len = vertical ? zone.height : zone.width;
    const start = vertical ? zone.top : zone.left;
    const gap = n < 2 ? 0.0 : Math.min(this.g, (0.25 * len) / (n - 1));
    const avail = len - gap * (n - 1);
    const mu = items.map((it) => (vertical ? 1 / aspectOf(it) : aspectOf(it)));
    const lam = mu.map((m) => Math.max(1.0, m));
    const eta = mu.map((m) => etaOf(m));
    const u = this.unit;
    const hi = items.map((it, i) =>
      Math.min(
        Math.min(
          Math.min(cross, maxL / lam[i]),
          cap === null ? Infinity : Math.max(cap(it), goodL) / eta[i],
        ),
        u === null ? Infinity : Math.max(UNIT_SLACK * u * it.sizeFactor * depth, minL) / eta[i],
      ),
    );
    const want = items.map((it, i) => it.sizeFactor / (this.fMax * eta[i]));
    const length = (c: number[]) => {
      let s = 0.0;
      for (let i = 0; i < n; i++) s += mu[i] * c[i];
      return s;
    };

    let sizes: number[] | null = null;
    const lo = hi.map((hv, i) => Math.min(hv, minL / eta[i]));
    if (length(lo) <= avail) {
      const at = (t: number) => hi.map((hv, i) => Math.min(hv, Math.max(lo[i], t * want[i])));
      let tLo = 0.0;
      let tHi = Math.max(...hi.map((hv, i) => hv / want[i]));
      if (length(at(tHi)) <= avail) {
        tLo = tHi;
      } else {
        for (let it = 0; it < 30; it++) {
          const t = (tLo + tHi) / 2;
          if (length(at(t)) <= avail) tLo = t;
          else tHi = t;
        }
      }
      sizes = at(tLo);
    }
    if (sizes === null) {
      const lo2 = hi.map((hv, i) => Math.min(hv, minL / lam[i]));
      const k = avail / length(lo2);
      sizes = lo2.map((c) => c * k);
    }

    const mains = sizes.map((s, i) => mu[i] * s);
    const used = mains.reduce((a, b) => a + b);
    let space = gap;
    let pos = start + (len - used - gap * (n - 1)) / 2;
    if (justify && (len - used) / (n + 1) >= gap) {
      space = (len - used) / (n + 1);
      pos = start + space;
    }
    if (stagger !== 0 && n > 1) {
      const total = used + space * (n - 1);
      const half = total / n / 2;
      const fitsRight = pos + total + half <= start + len;
      const fitsLeft = pos - half >= start;
      if (fitsRight && (stagger > 0 || !fitsLeft)) pos += half;
      else if (fitsLeft) pos -= half;
    }
    const crossStart = vertical ? zone.left : zone.top;
    const anchor = vertical ? new Offset(align, 0.5) : new Offset(0.5, align);
    const alone = n === 1 && wobble.length > 0 && !vertical;
    if (n < 2) wobble = [];
    const shift = new Array<number>(n).fill(0);
    const free = len - used - space * (n - 1);
    if (!vertical && wobble.length > 0 && free > 0) {
      const wts: number[] = [];
      for (let i = 1; i < n; i++) wts.push(1 + SPREAD[i % 6]);
      const sum = wts.reduce((a, b) => a + b);
      for (let i = 1; i < n; i++) shift[i] = shift[i - 1] + (0.4 * free * wts[i - 1]) / sum;
      const mid = shift[n - 1] / 2;
      for (let i = 0; i < n; i++) shift[i] -= mid;
    }
    const out: P[] = [];
    for (let i = 0; i < n; i++) {
      const c0 = crossStart + (cross - sizes[i]) * align;
      const d =
        wobble.length === 0 ? 0.0 : wobble[i % wobble.length] * (vertical ? cross - sizes[i] : cross);
      out.push(
        new P(
          items[i],
          vertical ? Rect.fromLTWH(c0, pos, sizes[i], mains[i]) : Rect.fromLTWH(pos, c0, mains[i], sizes[i]),
          anchor,
          vertical ? new Offset(d, 0) : new Offset(shift[i], d),
          alone,
          depth,
        ),
      );
      pos += mains[i] + space;
    }
    return out;
  }

  resolve(ps: P[]): Plan {
    const out: P[] = [];
    let bad = 0;
    for (const p of ps) {
      let r = p.rect;
      const clash = (x: Rect) => out.some((a) => coversTooMuch(a.rect, x));
      if (clash(r)) {
        let lo = Math.min(1.0, this.minL / long(r));
        let hi = 1.0;
        if (clash(shrink(r, lo, p.anchor))) {
          bad++;
        } else {
          for (let it = 0; it < 14; it++) {
            const m = (lo + hi) / 2;
            if (clash(shrink(r, m, p.anchor))) hi = m;
            else lo = m;
          }
        }
        r = shrink(r, lo, p.anchor);
      }
      if (long(r) < this.minL - 0.5) bad++;
      out.push(p.at(r));
    }
    return new Plan(out, bad, out.map((p) => readable(p.rect)).sort(cmp));
  }
}

function* cutsOf(n: number, k: number, from = 1): Generator<number[]> {
  if (k === 1) {
    yield [];
    return;
  }
  for (let c = from; c <= n - k + 1; c++) {
    for (const rest of cutsOf(n, k - 1, c + 1)) yield [c, ...rest];
  }
}

const aspectOf = (it: LayoutItem) => clamp(itemAspect(it), 0.1, 10.0);

const long = (r: Rect) => Math.max(r.width, r.height);

const area = (r: Rect) => (r.width <= 0 || r.height <= 0 ? 0 : r.width * r.height);

function readable(r: Rect): number {
  return long(r) * thin(long(r), r.shortestSide);
}

function thin(lng: number, short: number): number {
  return lng <= 0 ? 1 : Math.min(1.0, Math.max((2.5 * short) / lng, 0.625));
}

function etaOf(mu: number): number {
  return Math.max(1.0, mu) * thin(Math.max(1.0, mu), Math.min(1.0, mu));
}

function shrink(r: Rect, f: number, anchor: Offset): Rect {
  const ax = r.left + r.width * anchor.dx;
  const ay = r.top + r.height * anchor.dy;
  return Rect.fromLTWH(ax - r.width * f * anchor.dx, ay - r.height * f * anchor.dy, r.width * f, r.height * f);
}

function clampInto(r: Rect, bounds: Rect): Rect {
  let dx = 0.0;
  let dy = 0.0;
  if (r.left < bounds.left) dx = bounds.left - r.left;
  if (r.right + dx > bounds.right) dx = bounds.right - r.right;
  if (r.top < bounds.top) dy = bounds.top - r.top;
  if (r.bottom + dy > bounds.bottom) dy = bounds.bottom - r.bottom;
  return r.shift(new Offset(dx, dy));
}
